import { useCallback, useEffect, useRef, useState } from 'react'
import { format } from 'date-fns'
import type { DateRange } from 'react-day-picker'

import { useCampaignSelection } from '@/entities/campaign'
import { useChatCampaignDates, useChatDashboardMetrics, type ChatMetricsDataItem, type ChatDispositionItem, type ChatDurationItem } from '@/entities/chatMetric'
import { CampaignSelector } from '@/components/features/CampaignSelector'
import { DateRangePicker } from '@/pages/dashboard/ui'

const fmt = 'yyyy-MM-dd'

function parseHashParams(): { startDate?: string; endDate?: string } {
  const hash = window.location.hash.slice(1)
  if (!hash) return {}
  const params = new URLSearchParams(hash)
  return {
    startDate: params.get('start') || undefined,
    endDate: params.get('end') || undefined,
  }
}

function updateHashParams(startDate?: string, endDate?: string) {
  const params = new URLSearchParams()
  if (startDate) params.set('start', startDate)
  if (endDate) params.set('end', endDate)
  const hash = params.toString()
  window.history.replaceState(null, '', hash ? `#${hash}` : window.location.pathname)
}

function num(v: number | undefined, digits = 0): string {
  if (v === undefined || v === null || isNaN(v)) return '-'
  return digits ? v.toFixed(digits) : String(Math.round(v))
}

function MetricsTable({ metricsData }: { metricsData: ChatMetricsDataItem[] }) {
  if (!metricsData.length) return null
  const keys = ['Total sessions', 'Closed sessions', 'Active sessions', 'Successful sessions', 'Success rate', 'Total user messages', 'Total assistant messages', 'Avg messages per session']
  return (
    <div className="mb-6 bg-white rounded-lg shadow-sm p-4 border">
      <h3 className="text-body font-semibold mb-3">Session Metrics</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-detail">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 pr-4">Metric</th>
              {metricsData.map(d => <th key={d.date} className="text-right py-2 px-2">{d.date}</th>)}
            </tr>
          </thead>
          <tbody>
            {keys.map(k => (
              <tr key={k} className="border-b last:border-0">
                <td className="py-2 pr-4 font-medium">{k}</td>
                {metricsData.map(d => (
                  <td key={d.date} className="text-right py-2 px-2">
                    {k === 'Success rate'
                      ? `${num((d.generalMetrics[k] ?? 0) * 100, 1)}%`
                      : num(d.generalMetrics[k], k === 'Avg messages per session' ? 1 : 0)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function LatencyTable({ metricsData }: { metricsData: ChatMetricsDataItem[] }) {
  if (!metricsData.length) return null
  const keys = ['Avg response latency', 'p50 response latency', 'p95 response latency']
  return (
    <div className="mb-6 bg-white rounded-lg shadow-sm p-4 border">
      <h3 className="text-body font-semibold mb-3">Response Latency (s)</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-detail">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 pr-4">Metric</th>
              {metricsData.map(d => <th key={d.date} className="text-right py-2 px-2">{d.date}</th>)}
            </tr>
          </thead>
          <tbody>
            {keys.map(k => (
              <tr key={k} className="border-b last:border-0">
                <td className="py-2 pr-4 font-medium">{k}</td>
                {metricsData.map(d => (
                  <td key={d.date} className="text-right py-2 px-2">{num(d.latencyMetrics[k], 2)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function DurationTable({ durationMetrics }: { durationMetrics: ChatDurationItem[] }) {
  if (!durationMetrics.length) return null
  const keys = ['Avg session length', '<1min', '1-5min', '5-15min', '>=15min']
  return (
    <div className="mb-6 bg-white rounded-lg shadow-sm p-4 border">
      <h3 className="text-body font-semibold mb-3">Session Duration</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-detail">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 pr-4">Metric</th>
              {durationMetrics.map(d => <th key={d.date} className="text-right py-2 px-2">{d.date}</th>)}
            </tr>
          </thead>
          <tbody>
            {keys.map(k => (
              <tr key={k} className="border-b last:border-0">
                <td className="py-2 pr-4 font-medium">{k}</td>
                {durationMetrics.map(d => (
                  <td key={d.date} className="text-right py-2 px-2">
                    {k === 'Avg session length' ? `${num(d.metrics[k], 1)}s` : num(d.metrics[k])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function DispositionTable({ dispositionMetrics }: { dispositionMetrics: ChatDispositionItem[] }) {
  if (!dispositionMetrics.length) return null
  const allKeys = new Set<string>()
  dispositionMetrics.forEach(d => Object.keys(d.rawDispositions).forEach(k => allKeys.add(k)))
  const keys = [...allKeys]
  if (!keys.length) return null
  return (
    <div className="mb-6 bg-white rounded-lg shadow-sm p-4 border">
      <h3 className="text-body font-semibold mb-3">Session End Reasons</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-detail">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 pr-4">Reason</th>
              {dispositionMetrics.map(d => <th key={d.date} className="text-right py-2 px-2">{d.date}</th>)}
            </tr>
          </thead>
          <tbody>
            {keys.map(k => (
              <tr key={k} className="border-b last:border-0">
                <td className="py-2 pr-4 font-medium">{k}</td>
                {dispositionMetrics.map(d => (
                  <td key={d.date} className="text-right py-2 px-2">{num(d.rawDispositions[k])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function ChatDashboard() {
  const { campaignId, setCampaignId, campaigns, campaignsLoading } = useCampaignSelection()

  const initialHash = useRef(parseHashParams())
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const { startDate, endDate } = initialHash.current
    if (startDate && endDate) {
      return {
        from: new Date(startDate + 'T00:00:00'),
        to: new Date(endDate + 'T00:00:00'),
      }
    }
    return undefined
  })
  const hasSubmittedInitialRef = useRef(!!initialHash.current.startDate)

  const { data: campaignDates, isLoading: datesLoading } = useChatCampaignDates(campaignId)

  const startDateStr = dateRange?.from ? format(dateRange.from, fmt) : null
  const endDateStr = dateRange?.to ? format(dateRange.to, fmt) : null

  const { data: metricsData, isLoading: metricsLoading, isFetching } =
    useChatDashboardMetrics(campaignId, startDateStr, endDateStr)

  useEffect(() => {
    updateHashParams(startDateStr || undefined, endDateStr || undefined)
  }, [startDateStr, endDateStr])

  useEffect(() => {
    if (campaignDates && !hasSubmittedInitialRef.current) {
      const from = new Date(campaignDates.startDate + 'T00:00:00')
      const to = new Date(campaignDates.endDate + 'T00:00:00')
      setDateRange({ from, to })
      hasSubmittedInitialRef.current = true
    }
  }, [campaignDates])

  useEffect(() => {
    if (!initialHash.current.startDate) hasSubmittedInitialRef.current = false
    initialHash.current = {}
  }, [campaignId])

  const handleCampaignChange = useCallback((id: number | null) => {
    setCampaignId(id)
    hasSubmittedInitialRef.current = false
  }, [setCampaignId])

  const handleDateRangeChange = useCallback((range: DateRange | undefined) => {
    if (!range) return
    setDateRange(range)
  }, [])

  const initialLoading = (campaignsLoading || datesLoading || metricsLoading) && !metricsData

  return (
    <div className="w-full h-full p-6 overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex-shrink-0">
          <h1 className="text-heading font-bold mb-1">Daily Metrics — Chat</h1>
          <p className="text-body text-muted-foreground">Monitor text conversation performance</p>
        </div>
        <CampaignSelector
          campaigns={campaigns}
          selectedCampaignId={campaignId}
          onCampaignChange={handleCampaignChange}
          className="w-[400px]"
          disabled={campaignsLoading}
        />
      </div>

      <div className="flex items-center gap-2 mb-4">
        <DateRangePicker dateRange={dateRange} onDateRangeChange={handleDateRangeChange} />
        {isFetching && !initialLoading && (
          <div className="flex items-center gap-2 text-body text-muted-foreground">
            <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
            Loading...
          </div>
        )}
      </div>

      {initialLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-body text-muted-foreground">Loading chat dashboard...</div>
        </div>
      ) : !campaignId ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-body text-muted-foreground">Select a campaign to view metrics</div>
        </div>
      ) : !metricsData?.metricsData?.length ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-body text-muted-foreground">No chat data for the selected date range</div>
        </div>
      ) : (
        <div className={isFetching ? 'opacity-60 transition-opacity duration-200' : 'transition-opacity duration-200'}>
          <MetricsTable metricsData={metricsData.metricsData} />
          <LatencyTable metricsData={metricsData.metricsData} />
          <DurationTable durationMetrics={metricsData.durationMetrics} />
          <DispositionTable dispositionMetrics={metricsData.dispositionMetrics} />
        </div>
      )}
    </div>
  )
}
